<template>
    <div>
        <PageHeader>
            {{ $t('producerSubscriptions.title') }}
        </PageHeader>

        <DataTable :items="items" :fields="fields" :loading="loading" :error="error">
            <template #cell(permission)="{ item }">
                <p>{{ getPermissionName(item.permission) }}</p>
            </template>

            <template #cell(status)="{ item }">
                <div class="d-flex align-items-center">
                    <StatusLabel :type="item.statusType">
                        {{ $t(`producerSubscriptions.status.${item.status}`) }}
                    </StatusLabel>
                    <ActivatePermissionButton
                        v-if="item.status === 'inactive'"
                        class="ml-3"
                        :permission="item.permission"
                        :title="$t(item.activateTitle)"
                        :description="$t(item.activateDescription)"
                        @activated="onActivate"
                        @error="fetchData"
                    />
                </div>
            </template>

            <template #cell(action)="{ item }">
                <div class="text-right">
                    <CancelPermissionButton v-if="item.canCancel" :permission="item.permission" :permission-id="item.id" @canceled="onCancel" @error="fetchData" />
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script>
    import DataTable from '@/components/common/DataTable.vue';

    import spaceService from '@/services/spaceService';
    import StatusLabel from './parts/StatusLabel.vue';
    import ActivatePermissionButton from './parts/ActivatePermissionButton.vue';
    import CancelPermissionButton from './parts/CancelPermissionButton.vue';

    export default {
        name: 'Subscriptions',
        components: {
            DataTable,
            StatusLabel,
            ActivatePermissionButton,
            CancelPermissionButton,
        },
        data() {
            return {
                requestPermissions: this.$req('space/permissions'),
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            userId() {
                return this.$store.state.auth.user.id;
            },
            loading() {
                return this.requestPermissions.getLoading();
            },
            error() {
                return this.requestPermissions.getError();
            },

            fields() {
                return [
                    { key: 'permission', label: this.$t('producerSubscriptions.permissionName'), sortable: true },
                    { key: 'status', label: this.$t('producerSubscriptions.status') },
                    { key: 'action', label: '', sortable: false },
                ];
            },
            items() {
                const current = this.$store.getters['space/currentSpace'];
                const allPermissionKeys = Object.values(spaceService.permissions);
                const rawPermissions = this.requestPermissions.getData()?.items || [];

                const currentPermissions = rawPermissions.filter(p => p.space?.id === current?.id);

                const byPermission = Object.fromEntries(currentPermissions.map(p => [p.permission, p]));

                return allPermissionKeys.map(permissionName => {
                    const item = byPermission[permissionName] || {
                        permission: permissionName,
                        status: 'inactive',
                    };

                    const keys = spaceService.getPermissionContentKey(permissionName);
                    const isActive = current?.permissions?.includes(permissionName);
                    let status;
                    if (item.status === 'canceled') {
                        status = 'canceled';
                    } else if (item.status === 'active' || (item.status === 'inactive' && isActive)) {
                        status = 'active';
                    } else {
                        status = item.status;
                    }

                    return {
                        ...item,
                        status,
                        statusType: spaceService.getPermissionStatusLabelType(status),
                        activateTitle: keys.title,
                        activateDescription: keys.description,
                        canCancel: status === 'active' && !item.agreement,
                    };
                });
            },
        },
        watch: {
            spaceId: {
                handler: 'fetchData',
                immediate: true,
            },
        },
        methods: {
            getPermissionName(permission) {
                return this.$t(`producerSubscriptions.${permission.replace(/_(\w)/g, (match, letter) => letter.toUpperCase())}`);
            },

            onCancel() {
                this.$toastr.success(this.$t('producerSubscriptions.cancelSuccess'));
                this.fetchData();
            },

            onActivate() {
                this.$toastr.success(this.$t('producerSubscriptions.activateSuccess'));
                this.fetchData();
            },

            async fetchData() {
                await this.requestPermissions.get(this.userId);
            },
        },
    };
</script>
